// src/pages/tenant/TenantWebhookPage.jsx
// Phase 7 — M7: Tenant self-service webhook endpoint management

import { useEffect, useState, useCallback } from 'react'

const PINK = '#FF3D7F'
const ORG  = '#FF8500'
const DARK = '#1A1A2E'
const GREY = '#9CA3AF'
const GR   = '#F3F4F6'

const api = (path, opts = {}) =>
  fetch(`/api/v1${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
    ...opts,
  }).then(r => r.json())

const DELIVERY_STATUS = {
  success:  { bg: '#DCFCE7', color: '#166534' },
  failed:   { bg: '#FEE2E2', color: '#991B1B' },
  pending:  { bg: '#F3F4F6', color: '#6B7280' },
  retrying: { bg: '#FEF3C7', color: '#92400E' },
}

function StatusPill({ status }) {
  const s = DELIVERY_STATUS[status] ?? DELIVERY_STATUS.pending
  return <span style={{ background: s.bg, color: s.color, padding: '2px 9px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700 }}>{status}</span>
}

function EndpointCard({ endpoint, onPing, onPause, onResume, onDelete, onViewDeliveries }) {
  const statusColor = endpoint.status === 'active' ? '#10B981' : endpoint.status === 'paused' ? ORG : '#EF4444'
  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: 22, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', borderLeft: `4px solid ${statusColor}`, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <div style={{ fontWeight: 700, color: DARK, fontSize: '0.95rem', wordBreak: 'break-all' }}>{endpoint.url}</div>
          {endpoint.description && <div style={{ fontSize: '0.8rem', color: GREY, marginTop: 2 }}>{endpoint.description}</div>}
        </div>
        <span style={{ background: statusColor + '22', color: statusColor, padding: '3px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 800, flexShrink: 0, marginLeft: 10 }}>
          {endpoint.status.toUpperCase()}
        </span>
      </div>

      {/* Event types */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 14 }}>
        {(endpoint.event_types ?? []).map(t => (
          <span key={t} style={{ background: '#EDE9FE', color: '#5B21B6', padding: '2px 8px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 700, fontFamily: 'monospace' }}>{t}</span>
        ))}
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 14 }}>
        {[
          ['✓ Delivered', endpoint.success_count, '#10B981'],
          ['✗ Failed',    endpoint.failure_count, endpoint.failure_count > 0 ? '#EF4444' : GREY],
          ['Retries',     endpoint.max_retries,   GREY],
        ].map(([l, v, c]) => (
          <div key={l} style={{ padding: '8px 12px', background: GR, borderRadius: 9 }}>
            <div style={{ fontWeight: 800, color: c, fontSize: '0.95rem' }}>{v}</div>
            <div style={{ fontSize: '0.73rem', color: GREY }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => onViewDeliveries(endpoint)} style={{ padding: '6px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', background: '#fff', color: '#374151', fontWeight: 600, fontSize: '0.79rem', cursor: 'pointer' }}>
          📋 Deliveries
        </button>
        <button onClick={() => onPing(endpoint.id)} style={{ padding: '6px 12px', borderRadius: 9, border: `1.5px solid ${PINK}`, background: '#fff', color: PINK, fontWeight: 600, fontSize: '0.79rem', cursor: 'pointer' }}>
          🏓 Send Ping
        </button>
        {endpoint.status === 'active' && (
          <button onClick={() => onPause(endpoint.id)} style={{ padding: '6px 12px', borderRadius: 9, border: `1.5px solid ${ORG}`, background: '#fff', color: ORG, fontWeight: 600, fontSize: '0.79rem', cursor: 'pointer' }}>Pause</button>
        )}
        {endpoint.status === 'paused' && (
          <button onClick={() => onResume(endpoint.id)} style={{ padding: '6px 12px', borderRadius: 9, border: 'none', background: '#10B981', color: '#fff', fontWeight: 600, fontSize: '0.79rem', cursor: 'pointer' }}>Resume</button>
        )}
        <button onClick={() => onDelete(endpoint.id)} style={{ padding: '6px 10px', borderRadius: 9, border: 'none', background: '#FEE2E2', color: '#991B1B', fontWeight: 600, fontSize: '0.79rem', cursor: 'pointer', marginLeft: 'auto' }}>🗑</button>
      </div>
    </div>
  )
}

export default function TenantWebhookPage() {
  const [endpoints, setEndpoints]       = useState([])
  const [eventTypes, setEventTypes]     = useState([])
  const [deliveries, setDeliveries]     = useState([])
  const [selectedEndpoint, setSelected] = useState(null)
  const [loading, setLoading]           = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showSecretBanner, setShowSecretBanner] = useState(null)
  const [pingResult, setPingResult]     = useState(null)
  const [form, setForm]                 = useState({ url: '', description: '', event_types: ['*'], timeout_seconds: 30 })
  const [saving, setSaving]             = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const [epRes, etRes] = await Promise.all([
      api('/tenant/webhooks'),
      api('/tenant/webhooks/event-types'),
    ])
    setEndpoints(Array.isArray(epRes.data) ? epRes.data : [])
    setEventTypes(Array.isArray(etRes.data) ? etRes.data : [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const loadDeliveries = async (endpoint) => {
    setSelected(endpoint)
    const r = await api(`/tenant/webhooks/${endpoint.id}/deliveries`)
    setDeliveries(r.data?.data ?? [])
  }

  const register = async () => {
    setSaving(true)
    const r = await api('/tenant/webhooks', { method: 'POST', body: JSON.stringify(form) })
    setSaving(false)
    if (r.status === 'success' || r.data) {
      setShowAddModal(false)
      setShowSecretBanner(r.data)  // show secret once
      load()
    } else {
      alert(r.message ?? 'Registration failed')
    }
  }

  const ping = async (id) => {
    const r = await api(`/tenant/webhooks/${id}/ping`, { method: 'POST' })
    setPingResult(r.data ?? r)
    setTimeout(() => setPingResult(null), 5000)
  }

  const pause  = async (id) => { await api(`/tenant/webhooks/${id}`, { method: 'PUT', body: JSON.stringify({ status: 'paused' }) }); load() }
  const resume = async (id) => { await api(`/tenant/webhooks/${id}`, { method: 'PUT', body: JSON.stringify({ status: 'active' }) }); load() }
  const del    = async (id) => {
    if (!confirm('Delete this webhook endpoint? All delivery history will be removed.')) return
    await api(`/tenant/webhooks/${id}`, { method: 'DELETE' }); load()
    if (selectedEndpoint?.id === id) setSelected(null)
  }

  const toggleEventType = (type) => {
    setForm(f => {
      const types = [...(f.event_types ?? [])]
      if (type === '*') return { ...f, event_types: types.includes('*') ? [] : ['*'] }
      const idx = types.indexOf(type)
      if (idx > -1) types.splice(idx, 1)
      else { types.splice(types.indexOf('*'), 1); types.push(type) }
      return { ...f, event_types: types }
    })
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: GREY }}>Loading webhooks…</div>

  return (
    <div style={{ maxWidth: 1060, margin: '0 auto', padding: '0 0 56px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.75rem', color: DARK, margin: 0 }}>Webhook Endpoints</h1>
          <p style={{ color: GREY, margin: '4px 0 0', fontSize: '0.88rem' }}>Register outbound webhooks to receive real-time Fabrinza events in your systems</p>
        </div>
        <button onClick={() => setShowAddModal(true)} style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: PINK, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem' }}>
          + Register Endpoint
        </button>
      </div>

      {/* Secret banner */}
      {showSecretBanner && (
        <div style={{ background: '#FFF7ED', border: '2px solid #FED7AA', borderRadius: 14, padding: 20, marginBottom: 20 }}>
          <div style={{ fontWeight: 800, color: DARK, marginBottom: 8 }}>🔑 Signing Secret — Save this now!</div>
          <div style={{ fontSize: '0.82rem', color: '#92400E', marginBottom: 12 }}>This secret is shown only once. Store it securely — use it to verify webhook signatures.</div>
          <code style={{ display: 'block', background: '#fff', padding: '10px 14px', borderRadius: 9, fontFamily: 'monospace', fontSize: '0.87rem', color: DARK, wordBreak: 'break-all', border: '1.5px solid #FED7AA' }}>
            {showSecretBanner.secret}
          </code>
          <button onClick={() => setShowSecretBanner(null)} style={{ marginTop: 12, padding: '6px 14px', borderRadius: 8, border: 'none', background: ORG, color: '#fff', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
            I've saved it ✓
          </button>
        </div>
      )}

      {/* Ping result toast */}
      {pingResult && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: pingResult.outcome === 'delivered' ? '#10B981' : '#EF4444', color: '#fff', padding: '12px 20px', borderRadius: 12, fontWeight: 700, fontSize: '0.85rem', boxShadow: '0 4px 14px rgba(0,0,0,0.15)', zIndex: 1000 }}>
          {pingResult.outcome === 'delivered' ? `✓ Ping delivered (${pingResult.duration_ms}ms)` : `✗ Ping failed: ${pingResult.error ?? `HTTP ${pingResult.http_status}`}`}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: selectedEndpoint ? '1fr 420px' : '1fr', gap: 20 }}>
        {/* Endpoint list */}
        <div>
          {endpoints.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 18, padding: 56, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: 10 }}>🔗</div>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: DARK, marginBottom: 6 }}>No webhooks yet</div>
              <div style={{ fontSize: '0.84rem', color: GREY, marginBottom: 20 }}>Register an endpoint to start receiving real-time Fabrinza events in your systems.</div>
              <button onClick={() => setShowAddModal(true)} style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: PINK, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>+ Register First Endpoint</button>
            </div>
          ) : (
            endpoints.map(ep => (
              <EndpointCard key={ep.id} endpoint={ep} onPing={ping} onPause={pause} onResume={resume} onDelete={del} onViewDeliveries={loadDeliveries} />
            ))
          )}
        </div>

        {/* Delivery log panel */}
        {selectedEndpoint && (
          <div style={{ background: '#fff', borderRadius: 18, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', height: 'fit-content', position: 'sticky', top: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, color: DARK, fontSize: '0.9rem' }}>📋 Delivery Log</div>
              <button onClick={() => setSelected(null)} style={{ background: 'transparent', border: 'none', color: GREY, fontSize: '1rem', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ fontSize: '0.78rem', color: GREY, marginBottom: 14, wordBreak: 'break-all' }}>{selectedEndpoint.url}</div>
            {deliveries.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: GREY, fontSize: '0.84rem' }}>No deliveries yet.</div>
            ) : (
              <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                {deliveries.map(d => (
                  <div key={d.id} style={{ borderBottom: '1px solid #F3F4F6', padding: '10px 0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#5B21B6' }}>{d.event_type}</span>
                      <StatusPill status={d.status} />
                    </div>
                    <div style={{ display: 'flex', gap: 12, fontSize: '0.75rem', color: GREY }}>
                      <span>Attempt #{d.attempt}</span>
                      {d.http_status && <span>HTTP {d.http_status}</span>}
                      {d.duration_ms && <span>{d.duration_ms}ms</span>}
                    </div>
                    {d.error_message && <div style={{ fontSize: '0.73rem', color: '#991B1B', marginTop: 3, wordBreak: 'break-word' }}>{d.error_message}</div>}
                    <div style={{ fontSize: '0.72rem', color: '#D1D5DB', marginTop: 3 }}>{d.created_at ? new Date(d.created_at).toLocaleString('en-IN') : ''}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Register endpoint modal */}
      {showAddModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', fontWeight: 800, color: DARK, margin: '0 0 20px' }}>Register Webhook Endpoint</h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: GREY, marginBottom: 4 }}>Endpoint URL *</label>
                <input type="url" value={form.url} onChange={e => setForm(f => ({...f, url: e.target.value}))} placeholder="https://your-system.com/webhooks/fabrinza"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: GREY, marginBottom: 4 }}>Description</label>
                <input type="text" value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="e.g. Production order webhook"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: GREY, marginBottom: 8 }}>Event Types *</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {['*', ...eventTypes].map(t => {
                    const sel = (form.event_types ?? []).includes(t)
                    return (
                      <button key={t} onClick={() => toggleEventType(t)} style={{ padding: '4px 10px', borderRadius: 20, border: `1.5px solid ${sel ? PINK : '#E5E7EB'}`, background: sel ? PINK : '#fff', color: sel ? '#fff' : '#374151', fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer', fontFamily: t === '*' ? 'inherit' : 'monospace' }}>
                        {t === '*' ? '* All Events' : t}
                      </button>
                    )
                  })}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: GREY, marginBottom: 4 }}>Timeout (seconds)</label>
                <input type="number" min="5" max="60" value={form.timeout_seconds} onChange={e => setForm(f => ({...f, timeout_seconds: +e.target.value}))}
                  style={{ width: 100, padding: '8px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem' }} />
              </div>
            </div>

            <div style={{ background: '#F0FDF4', borderRadius: 10, padding: '10px 14px', fontSize: '0.8rem', color: '#166534', margin: '18px 0' }}>
              🔐 A signing secret will be generated and shown only once after registration. Use it to verify the <code>X-Fabrinza-Signature</code> header on incoming requests.
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowAddModal(false)} style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={register} disabled={saving || !form.url || (form.event_types ?? []).length === 0}
                style={{ flex: 2, padding: '10px', borderRadius: 10, border: 'none', background: saving || !form.url ? '#D1D5DB' : PINK, color: '#fff', fontWeight: 700, cursor: saving || !form.url ? 'default' : 'pointer' }}>
                {saving ? 'Registering…' : 'Register Endpoint'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
